import { useEffect, useState, type FormEvent } from "react";
import { api, errorMessage, isCode } from "../api/client.ts";
import type { SettingsData } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { IconPlus } from "../components/Icons.tsx";
import { TelegramSetting } from "../components/Telegram.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, PageHeader, Spinner } from "../components/ui.tsx";
import { useAsync } from "../lib/hooks.ts";
import { useSession } from "../lib/session.tsx";
import { rich, useT } from "../i18n/index.tsx";

const parseIps = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);

export function Settings() {
  const t = useT();
  const { can } = useSession();
  const edit = can("console_ip:edit");
  const q = useAsync(() => api.get<SettingsData>("/settings"), []);
  const [text, setText] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const toast = useToast();

  const saved = q.data?.console_ip_allowlist ?? [];
  useEffect(() => {
    if (q.data) setText(q.data.console_ip_allowlist.join("\n"));
  }, [q.data]);

  const list = parseIps(text);
  const dirty = list.join("\n") !== saved.join("\n");
  const yourIp = q.data?.your_ip ?? "";
  const hasMine = list.some((x) => x === yourIp || x === `${yourIp}/32`);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return setCodeError(t.settings.codeRequired);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      const r = await api.put<{ console_ip_allowlist: string[]; your_ip: string }>("/settings/console-ip", { ip_allowlist: list, code });
      q.setData((d) => (d ? { ...d, ...r } : d));
      setCode("");
      toast(r.console_ip_allowlist.length ? t.settings.ipSaved : t.settings.ipCleared);
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.settings.codeFailed);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page narrow">
      <PageHeader title={t.settings.title} eyebrow={q.data?.organization.name}>
        {t.settings.intro}
      </PageHeader>
      {q.error ? (
        <Alert tone="error" action={<button className="btn btn-ghost btn-sm" onClick={() => void q.reload()}>{t.common.retry}</button>}>{errorMessage(q.error)}</Alert>
      ) : (
        <section className="card" aria-labelledby="ip-h">
          <h2 id="ip-h" className="card-title">{t.settings.ipTitle}</h2>
          <p className="card-sub">
            {t.settings.ipSub}
          </p>

          {q.loading && !q.data ? (
            <span className="sk sk-lg" />
          ) : (
            <form className="form-stack ip-form" onSubmit={submit} noValidate>
              {saved.length === 0 && (
                <Alert tone="warn" title={t.settings.noRestriction}>{t.settings.noRestrictionBody}</Alert>
              )}
              <div className="your-ip">
                <span>{rich(t.settings.yourIp, {}, { ip: yourIp })}</span>
                {edit && !hasMine && (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setText((cur) => (cur.trim() ? `${cur.trim()}\n${yourIp}` : yourIp))}>
                    <IconPlus size={16} /> {t.settings.addMyIp}
                  </button>
                )}
              </div>
              <div className="field">
                <label htmlFor="org-ips">{t.settings.allowedNetworks}</label>
                <textarea id="org-ips" rows={6} className="mono-input" value={text} onChange={(e) => setText(e.target.value)} readOnly={!edit}
                  placeholder={"203.0.113.10\n198.51.100.0/24"} spellCheck={false} aria-describedby="org-ips-hint" />
                <p id="org-ips-hint" className="field-hint">
                  {t.settings.ipHint}
                </p>
              </div>
              {list.length > 0 && !hasMine && <Alert tone="info">{t.settings.ipNotInList}</Alert>}
              <Alert tone="info" title={t.settings.lockedOutTitle}>
                {t.settings.lockedOutBody}
              </Alert>
              {edit ? (
                <>
                  {dirty && <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} />}
                  {error && <Alert tone="error">{error}</Alert>}
                  <div className="btn-row">
                    <button type="submit" className="btn btn-primary" disabled={busy || !dirty}>{busy && <Spinner />} {t.settings.saveAllowlist}</button>
                    {dirty && <button type="button" className="btn btn-ghost" onClick={() => { setText(saved.join("\n")); setError(null); }} disabled={busy}>{t.settings.undoChanges}</button>}
                  </div>
                </>
              ) : (
                <p className="muted small">{t.settings.readOnly}</p>
              )}
            </form>
          )}
        </section>
      )}
      <TelegramSetting />
    </div>
  );
}
